"use client";

import { useEffect, useMemo, useRef, useState } from "react";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useAdmin } from "@/lib/admin/context";
import { useT } from "@/lib/i18n/context";
import { Icon } from "@/lib/icons/Icon";
import type {
  WDropDownMenuContent,
  WMenuItem,
} from "@/lib/api/cms-window-content";
import { wDropDownMenuTranslations, type WDropDownMenuTranslations } from "./WDropDownMenu.i18n";
import styles from "./WDropDownMenu.module.css";

/**
 * WDropDownMenu — mega-menu (typ `w_drop_down_menu`).
 *
 * **Struktura (do 5 poziomów):**
 *   - L1: poziomy bar (top items, hover/click otwiera panel)
 *   - L2: panel — każdy L2 item to sekcja (header + lista L3 pod spodem)
 *   - L3: lista wpisów pod L2, z cut-off `max_elements_on_level_three` (config)
 *   - L4, L5: zagnieżdżone pod L3 (renderowane przez rekursję `NestedItem`)
 *
 * **Panel rendered at `<nav>` level** (nie pod `<li>`) — żeby mógł zająć
 * pełną szerokość nav'a. Pojedynczy element, content zmienia się per `openId`.
 *
 * **Config (z `w_drop_down_menu.inc.php`):**
 *   - `max_depth_level` — twarde ograniczenie poziomów (1–5). Aplikowane w SQL
 *     i rekurencji backendowej. React renderuje co dostał.
 *   - `max_elements_on_level_three` — limit elementów L3 widocznych pod jednym L2.
 *     Po przekroczeniu — link "zobacz wszystko" do parent'a L2. `null` = bez limitu.
 *
 * **Active state:** computed frontend-side via `usePathname()` — `isActive`
 * (exact) + `isInActivePath` (ancestor).
 *
 * **DEV-only mock-drzewo:** gdy `NODE_ENV=development` + flag
 * `devMockDropDownTree` w AdminContext (toggle w AdminTopBar), do `items[]`
 * doczepiany jest sztuczny L1 z 5-poziomowym drzewem testowym. Banner wewnątrz
 * panelu informuje że to dev-only test data.
 */
export function WDropDownMenu({ content }: WDropDownMenuProps) {
  const currentPathname = usePathname() ?? "";
  const { devMockDropDownTree, hasScope } = useAdmin();
  const t = useT(wDropDownMenuTranslations);
  const [openId, setOpenId] = useState<number | null>(null);
  const rootRef = useRef<HTMLElement | null>(null);

  /* Mock-drzewo — wstrzykiwane TYLKO gdy:
       1. NODE_ENV === "development"
       2. Admin zalogowany (`hasScope("cms:edit")`) — visitor NIE widzi mock,
          bo nie ma dostępu do dev-bannera z toggle'em (gated przez `hasSession`
          w PublicLayout), więc nie mógłby go wyłączyć.
          Uwaga: `useAdmin().user` jest NIE-null nawet dla visitor'a (PublicLayout
          ustawia placeholder `anonymousUser`), więc check po user-id jest
          błędny — wartościowy sygnał to scope.
       3. Flag `devMockDropDownTree` ON (default true, toggle w dev-banerze). */
  const isAdmin = hasScope("cms:edit");
  const items = useMemo(() => {
    if (!content) return [];
    const isDev = process.env.NODE_ENV === "development";
    if (!isDev || !isAdmin || !devMockDropDownTree) return content.items;
    return [...content.items, buildMockDevTree(t.devBanner.mockL1Name)];
  }, [content, devMockDropDownTree, isAdmin, t.devBanner.mockL1Name]);

  /* Close on outside click / ESC. */
  useEffect(() => {
    if (openId === null) return;

    function onPointerDown(e: PointerEvent) {
      if (!rootRef.current) return;
      if (!rootRef.current.contains(e.target as Node)) setOpenId(null);
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") setOpenId(null);
    }

    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [openId]);

  /* Auto-zamknięcie po nawigacji. */
  useEffect(() => {
    setOpenId(null);
  }, [currentPathname]);

  /* Auto-zamknięcie gdy `openId` wskazuje na nieistniejący już item (np. mock
     L1 zniknął po wyłączeniu `devMockDropDownTree`). Bez tego panel byłby
     zawieszony na pustym referensie aż user kliknie/wyjedzie poza nav. */
  useEffect(() => {
    if (openId === null) return;
    if (!items.some((i) => i.id === openId)) setOpenId(null);
  }, [items, openId]);

  if (!content || items.length === 0) {
    return (
      <nav className={styles.empty} aria-label={content?.title ?? t.ariaLabelFallback}>
        {t.empty}
      </nav>
    );
  }

  const openItem =
    openId !== null ? items.find((i) => i.id === openId) ?? null : null;

  return (
    <nav
      ref={rootRef}
      className={styles.root}
      aria-label={content.title}
      onMouseLeave={() => setOpenId(null)}
    >
      <ul className={styles.topBar}>
        {items.map((item) => (
          <TopBarItem
            key={item.id}
            item={item}
            isOpen={openId === item.id}
            isMock={item.id === MOCK_L1_ID}
            t={t}
            onHover={() => {
              /* Hover na L1: gdy ma dzieci — pokaż jego panel; gdy nie —
                 zamknij dotychczas otwarty (nie zostawiaj „sierot"). Bez tej
                 ostatniej gałęzi przejście z item'a-z-panelem na item-bez-panelu
                 zostawiało poprzedni panel widoczny dopóki user nie wyjedzie
                 myszką poza całego nav'a. */
              if ((item.children ?? []).length > 0) {
                setOpenId(item.id);
              } else {
                setOpenId(null);
              }
            }}
            onToggle={() => {
              if ((item.children ?? []).length === 0) return;
              setOpenId((prev) => (prev === item.id ? null : item.id));
            }}
            currentPathname={currentPathname}
          />
        ))}
      </ul>
      {openItem && (
        <MegaPanel
          parent={openItem}
          isMock={openItem.id === MOCK_L1_ID}
          currentPathname={currentPathname}
          maxElementsOnL3={content.config.max_elements_on_level_three}
          t={t}
        />
      )}
    </nav>
  );
}

export type WDropDownMenuProps = {
  content: WDropDownMenuContent | null;
};

/* ─── L1 (top bar) ────────────────────────────────────────────────────── */

function TopBarItem({
  item,
  isOpen,
  isMock,
  onHover,
  onToggle,
  currentPathname,
  t,
}: {
  item: WMenuItem;
  isOpen: boolean;
  isMock: boolean;
  onHover: () => void;
  onToggle: () => void;
  currentPathname: string;
  t: WDropDownMenuTranslations;
}) {
  const hasChildren = (item.children ?? []).length > 0;
  const { isActive, isInActivePath } = computeActiveState(
    item.url,
    currentPathname,
  );

  const triggerClass = [
    styles.topBarLink,
    isActive ? styles.topBarLinkActive : "",
    !isActive && isInActivePath ? styles.topBarLinkInPath : "",
    isOpen ? styles.topBarLinkOpen : "",
    isMock ? styles.topBarLinkMock : "",
  ]
    .filter(Boolean)
    .join(" ");

  if (!hasChildren) {
    return (
      <li className={styles.topBarItem} onMouseEnter={onHover}>
        <ItemLink
          item={item}
          className={triggerClass}
          ariaCurrent={isActive ? "page" : undefined}
        >
          {item.name}
        </ItemLink>
      </li>
    );
  }

  /* L1 z dziećmi — unified UI dla desktop + mobile: nazwa jako link (klik =
     nawigacja do podstrony parent'a) + osobny chevron button (klik = toggle
     panel). Hover na całym `<li>` (przez `onMouseEnter` na <li> wyżej) nadal
     otwiera panel automatycznie na desktop — chevron jest tylko alternative
     trigger (klawiatura A11y + touch na mobile gdzie nie ma hover'a). */
  return (
    <li className={styles.topBarItem} onMouseEnter={onHover}>
      <ItemLink
        item={item}
        className={triggerClass}
        ariaCurrent={isActive ? "page" : undefined}
      >
        {item.name}
      </ItemLink>
      <button
        type="button"
        className={styles.chevronToggle}
        aria-expanded={isOpen}
        aria-haspopup="true"
        aria-label={t.submenuToggleLabel(item.name, isOpen)}
        onClick={onToggle}
      >
        <span className={styles.chevron} aria-hidden="true">
          <Icon name={isOpen ? "caret-up" : "caret-down"} />
        </span>
      </button>
    </li>
  );
}

/* ─── Mega-panel (full-width pod nav'em) ──────────────────────────────── */

function MegaPanel({
  parent,
  isMock,
  currentPathname,
  maxElementsOnL3,
  t,
}: {
  parent: WMenuItem;
  isMock: boolean;
  currentPathname: string;
  maxElementsOnL3: number | null;
  t: WDropDownMenuTranslations;
}) {
  return (
    <div className={styles.panel} role="region" aria-label={parent.name}>
      {isMock && (
        <div className={styles.devMockBanner} role="note">
          <strong>{t.devBanner.prefix}</strong> {t.devBanner.body}{" "}
          <em>{t.devBanner.iconLabel}</em>
          {t.devBanner.suffix}
        </div>
      )}
      <div className={styles.panelGrid}>
        {(parent.children ?? []).map((l2) => (
          <L2Section
            key={l2.id}
            item={l2}
            currentPathname={currentPathname}
            maxElementsOnL3={maxElementsOnL3}
            t={t}
          />
        ))}
      </div>
    </div>
  );
}

function L2Section({
  item,
  currentPathname,
  maxElementsOnL3,
  t,
}: {
  item: WMenuItem;
  currentPathname: string;
  maxElementsOnL3: number | null;
  t: WDropDownMenuTranslations;
}) {
  const { isActive, isInActivePath } = computeActiveState(
    item.url,
    currentPathname,
  );
  const children = item.children ?? [];
  const hasChildren = children.length > 0;

  const headerClass = [
    styles.l2Header,
    isActive ? styles.l2HeaderActive : "",
    !isActive && isInActivePath ? styles.l2HeaderInPath : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <section className={styles.l2Section}>
      <ItemLink
        item={item}
        className={headerClass}
        ariaCurrent={isActive ? "page" : undefined}
      >
        <span className={styles.l2HeaderText}>
          <span className={styles.l2HeaderName}>{item.name}</span>
          {item.subtitle ? (
            <span className={styles.l2HeaderSubtitle}>{item.subtitle}</span>
          ) : null}
        </span>
        <span className={styles.l2HeaderArrow} aria-hidden="true">
          {"->"}
        </span>
      </ItemLink>

      {hasChildren && (
        <NestedList
          items={children}
          level={3}
          currentPathname={currentPathname}
          maxElementsOnL3={maxElementsOnL3}
          parentForOverflow={item}
          t={t}
        />
      )}
    </section>
  );
}

/* ─── L3+ (rekursywny render dla L3, L4, L5) ──────────────────────────── */

/**
 * Render listy zagnieżdżonej. Wywoływana z `L2Section` dla poziomu 3, sama
 * siebie wywołuje rekurencyjnie dla L4/L5.
 *
 * **Cut-off `maxElementsOnL3`** aplikowany TYLKO na poziomie 3 (zgodnie z
 * nazwą configu + legacy `parseNodesRecursive`). L4/L5 zawsze pełne.
 *
 * **`parentForOverflow`** — gdy lista uciętą, link "zobacz wszystko"
 * prowadzi do parent'a (L2 url). Ignorowane dla L4/L5.
 */
function NestedList({
  items,
  level,
  currentPathname,
  maxElementsOnL3,
  parentForOverflow,
  t,
}: {
  items: WMenuItem[];
  level: number;
  currentPathname: string;
  maxElementsOnL3: number | null;
  parentForOverflow: WMenuItem;
  t: WDropDownMenuTranslations;
}) {
  const applyCutoff = level === 3 && maxElementsOnL3 !== null;
  const limit = applyCutoff ? maxElementsOnL3 : items.length;
  const visible = items.slice(0, limit);
  const hasOverflow = applyCutoff && items.length > limit;

  return (
    <ul className={styles.nestedList} data-level={level}>
      {visible.map((child) => (
        <NestedItem
          key={child.id}
          item={child}
          level={level}
          currentPathname={currentPathname}
          maxElementsOnL3={maxElementsOnL3}
          t={t}
        />
      ))}
      {hasOverflow && (
        <li className={styles.nestedItem}>
          <ItemLink item={parentForOverflow} className={styles.seeAll}>
            {t.seeAll}
            <span aria-hidden="true">{" ->"}</span>
          </ItemLink>
        </li>
      )}
    </ul>
  );
}

function NestedItem({
  item,
  level,
  currentPathname,
  maxElementsOnL3,
  t,
}: {
  item: WMenuItem;
  level: number;
  currentPathname: string;
  maxElementsOnL3: number | null;
  t: WDropDownMenuTranslations;
}) {
  const { isActive } = computeActiveState(item.url, currentPathname);
  const children = item.children ?? [];
  const hasChildren = children.length > 0;

  const linkClass = [
    styles.nestedLink,
    isActive ? styles.nestedLinkActive : "",
  ]
    .filter(Boolean)
    .join(" ");

  return (
    <li className={styles.nestedItem} data-level={level}>
      <ItemLink
        item={item}
        className={linkClass}
        ariaCurrent={isActive ? "page" : undefined}
      >
        {item.name}
      </ItemLink>
      {hasChildren && (
        <NestedList
          items={children}
          level={level + 1}
          currentPathname={currentPathname}
          maxElementsOnL3={maxElementsOnL3}
          parentForOverflow={item}
          t={t}
        />
      )}
    </li>
  );
}

/* ─── Link helper (internal Link vs external <a>) ─────────────────────── */

function ItemLink({
  item,
  className,
  ariaCurrent,
  children,
}: {
  item: WMenuItem;
  className: string;
  ariaCurrent?: "page";
  children: React.ReactNode;
}) {
  const internalHref = extractInternalHref(item.url, item.target, item.type);
  if (internalHref !== null) {
    return (
      <Link href={internalHref} className={className} aria-current={ariaCurrent}>
        {children}
      </Link>
    );
  }
  return (
    <a
      href={item.url}
      target={item.target ?? undefined}
      rel={item.target === "_blank" ? "noopener noreferrer" : undefined}
      className={className}
      aria-current={ariaCurrent}
    >
      {children}
    </a>
  );
}

function extractInternalHref(rawUrl: string, target: string | null, type: number): string | null {
  if (!rawUrl) return null;
  /* type=4 = `imp_cms_catalog.link` (external URL) — patrz komentarz w `WMenu.tsx`. */
  if (type === 4) return null;
  if (target) return null;
  if (rawUrl.startsWith("/")) return rawUrl;
  try {
    const parsed = new URL(rawUrl);
    if (parsed.protocol !== "http:" && parsed.protocol !== "https:") return null;
    return parsed.pathname + parsed.search + parsed.hash;
  } catch {
    return null;
  }
}

function computeActiveState(
  itemUrl: string,
  currentPathname: string,
): { isActive: boolean; isInActivePath: boolean } {
  let itemPath: string;
  try {
    itemPath = new URL(itemUrl).pathname;
  } catch {
    itemPath = itemUrl;
  }
  const normalize = (p: string) =>
    p.length > 1 && p.endsWith("/") ? p.slice(0, -1) : p;
  const item = normalize(itemPath);
  const current = normalize(currentPathname);

  const isActive = current === item;
  const isInActivePath = !isActive && current.startsWith(item + "/");

  return { isActive, isInActivePath };
}

/* ─── DEV-only mock-drzewo ────────────────────────────────────────────── */

/** Sentinel ID dla wstrzykniętego dev-entry — używany żeby panel pokazał
    banner + żeby trigger L1 miał distinct style. Wartość ujemna nie koliduje
    z prawdziwymi `imp_cms_catalog.id` (zawsze > 0). */
const MOCK_L1_ID = -100000;

/**
 * Buduje sztuczny L1 entry z 5-poziomowym drzewem do wizualnego testowania
 * mega-menu w dev'ie. Każdy poziom ma href `#dev-mock` (no-op).
 *
 * Struktura: 1×L1 -> 3×L2 -> 3×L3 each -> 2×L4 each -> 2×L5 each.
 * Łącznie: 1 + 3 + 9 + 18 + 36 = 67 nodes (gęsto, ale realistycznie).
 *
 * @param l1Name nazwa L1 entry (per locale, z `t.devBanner.mockL1Name`)
 */
function buildMockDevTree(l1Name: string): WMenuItem {
  let nextId = MOCK_L1_ID - 1;
  const makeId = () => nextId--;

  const buildLevel = (
    level: number,
    parentLabel: string,
    countPerLevel: Record<number, number>,
  ): WMenuItem[] => {
    if (level > 5) return [];
    const count = countPerLevel[level] ?? 0;
    return Array.from({ length: count }, (_, i) => {
      const label = `${parentLabel}.${i + 1}`;
      return {
        id: makeId(),
        name: `Mock L${level} — ${label}`,
        url: `#dev-mock-${label}`,
        type: 1,
        target: null,
        id_catalog: null,
        children: buildLevel(level + 1, label, countPerLevel),
      };
    });
  };

  return {
    id: MOCK_L1_ID,
    name: l1Name,
    url: "#dev-mock",
    type: 1,
    target: null,
    id_catalog: null,
    children: buildLevel(2, "A", { 2: 3, 3: 3, 4: 2, 5: 2 }),
  };
}
